import { TikTokPrivacyLevels } from '../integrations/integrations.types';
import { CampaignChannel } from './campaign.types';

export enum CampaignSocials {
  FACEBOOK = 'Facebook',
  INSTAGRAM = 'Instagram',
  TIKTOK = 'TikTok',
}

export enum TikTokInteractions {
  COMMENT = 'Comment',
  DUET = 'Duet',
  STITCH = 'Stitch',
}

export enum SocialStatus {
  PUBLISHING = 'Publishing',
  FAILED = 'Failed',
  SUCCESSFULL = 'Successful',
}

export enum EmailStatus {
  Success = 'Success',
  AccountSuspended = 'AccountSuspended',
  AccountThrottled = 'AccountThrottled',
  Failed = 'Failed',
}

export interface SocialAccounts {
  [CampaignSocials.INSTAGRAM]: string | null;
  [CampaignSocials.FACEBOOK]: string | null;
  [CampaignSocials.TIKTOK]: string | null;
}

export interface AccountsDetails {
  name: string;
  username?: string;
  picture: string;
}

export interface SocialAccountsDetails {
  [CampaignSocials.INSTAGRAM]: AccountsDetails | null;
  [CampaignSocials.FACEBOOK]: AccountsDetails | null;
  [CampaignSocials.TIKTOK]: AccountsDetails | null;
}

export interface BaseSocialContent {
  media?: ContentMedia;
  caption: string;
  hashtags: string[];
  account?: string | null;
  accountDetail?: AccountsDetails | null;
}

export interface TikTokSocialContent extends BaseSocialContent {
  privacy: TikTokPrivacyLevels;
  interactions: TikTokInteractions[];
  discloseContent?: boolean;
  brand?: boolean;
  brandedContent?: boolean;
}

export interface SocialWiseContent {
  [CampaignSocials.INSTAGRAM]?: BaseSocialContent;
  [CampaignSocials.FACEBOOK]?: BaseSocialContent;
  [CampaignSocials.TIKTOK]?: TikTokSocialContent;
}

export interface SocialContent extends BaseSocialContent, SocialWiseContent {
  socials: CampaignSocials[];
  accounts: SocialAccounts;
  accountDetails?: SocialAccountsDetails;
}

export interface TextContent {
  body: string;
}

export interface CallToAction {
  text: string;
  url: string;
}

export interface IMediaMetaData {
  width: number;
  height: number;
  aspectRatio: string;
  resolution?: string;
  duration?: number;
  maxSeconds?: number;
}

export interface ContentMedia {
  type: string;
  url: string;
  name: string;
  metaData?: IMediaMetaData;
}

export interface EmailUrls {
  domain: string;
  facebook: string;
  instagram: string;
  twitter: string;
  yelp: string;
  logo: string;
}

// export interface EmailContent {
//   subject: string;
//   header: string;
//   subHeader: string;
//   body: string;
//   action: CallToAction;
//   media?: ContentMedia;
//   urls: EmailUrls;
// }

interface FontStyle {
  bold?: boolean;
  italic?: boolean;
  fontSize?: number;
  textFill?: string | null;
  alignment?: 'left' | 'center' | 'right' | null;
  lineHeight?: string | null;
  buttonFill?: string | null;
}

interface ItemSection {
  itemSectionEnabled?: boolean;
  layOut: 'right' | 'left';
  ctaImagePosition?: {
    x?: number;
    y?: number;
  } | null;
  headingEnabled?: boolean;
  headingText?: string;
  headingFontStyle?: FontStyle;
  subheadingEnabled?: boolean;
  subheadingText?: string;
  bodyEnabled?: boolean;
  bodyText?: string;
  bodyFontStyle?: FontStyle;
  ctaEnabled?: boolean;
  ctaButtonType?: 'primary' | 'secondary' | 'tertiary';
  ctaText?: string;
  ctaFontStyle?: FontStyle;
  ctaImageURL?: string | null;
  ctaBackgroundColor?: string | null;
}

export interface EmailContentV1 {
  subject: string;
  header: string;
  subHeader: string;
  body: string;
  action: CallToAction;
  media?: ContentMedia;
  urls: EmailUrls;
}

export interface EmailContent {
  subject: string;
  templateUrl?: string;
  globalSettings: {
    sectionCorners?: string;
    ctaCorners?: string;
  };
  header: {
    headerEnabled?: boolean;
    alignment?: 'left' | 'center' | 'right';
    logoUrl?: string;
    backgroundFill?: string;
    logoSize?: number;
  };
  hero: {
    heroSectionEnabled?: boolean;
    heroTextEnabled?: boolean;
    heroText?: string;
    heroFontStyle?: FontStyle;
    fillType?: 'color' | 'image';
    imageUrl?: string | null;
    colorHex?: string | null;
  };
  cta: {
    ctaEnabled?: boolean;
    alignment?: 'left' | 'center' | 'right';
    fillType?: 'color' | 'image';
    imageUrl?: string | null;
    colorHex?: string | null;
    headingEnabled?: boolean;
    headingText?: string;
    headingFontStyle?: FontStyle;
    subheadingEnabled?: boolean;
    subheadingText?: string;
    bodyEnabled?: boolean;
    bodyText?: string;
    bodyFontStyle?: FontStyle;
    callToActionEnabled?: boolean;
    ctaButtonType?: 'primary' | 'secondary' | 'tertiary';
    ctaText?: string;
    ctaFontStyle?: FontStyle;
    ctaURL?: string;
  };
  itemSections?: ItemSection[];
  footer: {
    alignment?: 'left' | 'center' | 'right';
    instagramEnabled?: boolean;
    xEnabled?: boolean;
    youtubeEnabled?: boolean;
    textFill?: string;
    iconFill?: string;
    hyperlinkFill?: string;
    backgroundFill?: 'color' | 'image';
    backgroundColor?: string | null;
    backgroundImage?: string | null;
    logoUrl?: string;
    logoSize?: number;
  };
}

export interface WebsiteContent {
  headline: string;
  subText?: string;
  body?: string;
  action: CallToAction;
  media?: ContentMedia;
}

export interface OnlineContent {
  action: CallToAction;
  media?: ContentMedia;
}

export interface KioskBannerContent {
  dynamicBanner: boolean;
  media?: ContentMedia;
}

export interface AppContent {
  media?: ContentMedia;
}

export interface ICampaignContentType {
  channel: CampaignChannel;
  details:
    | SocialContent
    | TextContent
    | EmailContent
    | AppContent
    | WebsiteContent
    | OnlineContent
    | KioskBannerContent;
  results?: SocialResult | TextResult | EmailResult;
}

export interface CampaignContentType {
  channel: CampaignChannel;
  content:
    | SocialContent
    | TextContent
    | EmailContent
    | AppContent
    | WebsiteContent
    | OnlineContent
    | KioskBannerContent;
}

export interface GenerateContent {
  data: ICampaignContentType[];
}

export interface SocialResult {
  [CampaignSocials.FACEBOOK]: {
    status: SocialStatus;
    reason?: string;
  };
  [CampaignSocials.INSTAGRAM]: {
    status: SocialStatus;
    reason?: string;
  };
  [CampaignSocials.TIKTOK]: {
    status: SocialStatus;
    reason?: string;
  };
}

export interface TextResult {
  total: number;
  sent: number;
  failed: number;
}

export interface EmailResult {
  total: number;
  sent: number;
  failed: number;
}
